import React from 'react';
import SizedView from './SizedView';
import Stack from './Stack';
import { View } from 'react-native';
import { getSpacing } from '../../utils/spacing';

export type DefinedItemWidthProps = {
    itemWidth: number;
};

export type DefinedItemsPerRowProps = {
    itemsPerRow: number;
};

export type KeyedNode = { gridItemKey: string; node: React.ReactNode };

const isKeyedNode = (node: React.ReactNode | KeyedNode): node is KeyedNode => {
    return (node as KeyedNode).gridItemKey !== undefined;
};

export type GridDisplayOwnProps = {
    items: (React.ReactNode | KeyedNode)[];
    padding: number;
    justifyRows?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
} & (DefinedItemWidthProps | DefinedItemsPerRowProps);

export type GridDisplayProps = GridDisplayOwnProps;

const GridDisplay: React.FC<GridDisplayProps> = (props) => {
    const calculateItemWidth = (containerWidth: number) => {
        if ('itemsPerRow' in props) {
            if (!containerWidth) return 0;

            return (containerWidth - getSpacing(props.padding) * (props.itemsPerRow - 1)) / props.itemsPerRow;
        } else {
            return props.itemWidth;
        }
    };

    const getRowItemsWidth = (containerWidth: number, itemsPerRow: number) => {
        return itemsPerRow * calculateItemWidth(containerWidth) + (itemsPerRow - 1) * getSpacing(props.padding);
    };

    const getItemsPerRow = (containerWidth: number) => {
        if (!containerWidth) return 1;

        let itemsPerRow = 0;

        do {
            itemsPerRow++;
        } while (getRowItemsWidth(containerWidth, itemsPerRow) <= containerWidth);

        return Math.max(itemsPerRow - 1, 1);
    };

    const getItemsChunked = (containerWidth: number): KeyedNode[][] => {
        const itemsPerRow = getItemsPerRow(containerWidth);
        const itemsChunked: KeyedNode[][] = [];

        for (let i = 0; i < props.items.length; i += itemsPerRow) {
            itemsChunked.push(
                props.items.slice(i, i + itemsPerRow).map((item, rowIndex) => {
                    if (isKeyedNode(item)) {
                        return item;
                    }

                    return {
                        gridItemKey: `${i}-${rowIndex}`,
                        node: item,
                    };
                }),
            );
        }

        return itemsChunked;
    };

    return (
        <SizedView style={{ width: '100%' }}>
            {({ width }) => (
                <Stack vertical space={props.padding} fullWidth>
                    {getItemsChunked(width).map((items, index) => (
                        <Stack horizontal justify={props.justifyRows || 'center'} key={index} fullWidth>
                            <Stack
                                horizontal
                                justify={'flex-start'}
                                space={props.padding}
                                style={{ width: getRowItemsWidth(width, getItemsPerRow(width)) }}
                            >
                                {items.map((item) => (
                                    <View key={item.gridItemKey} style={{ width: calculateItemWidth(width) }}>
                                        <React.Fragment>{item.node}</React.Fragment>
                                    </View>
                                ))}
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            )}
        </SizedView>
    );
    return null;
};

export default React.memo(GridDisplay);
